import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { MapPin, Clock, Users, Phone, Mail, Car, CheckCircle, Calendar, ChevronRight, Menu, X, Volume2, Navigation, Globe, Briefcase, XCircle, Check, Sun, Moon, History, Gift, Plane, Plus, BarChart3, TrendingUp, MoreVertical, Settings, LogOut, Smartphone, Key, TestTube, Bell, BellOff, FileText } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Calendar as CalendarComponent } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "./components/ui/dropdown-menu";
import { format } from "date-fns";

// Import translations from external files
import translations, { availableLanguages, detectBrowserLanguage } from "./locales";

// App Version - Update this to force cache refresh
const APP_VERSION = "3.1.0-i18n-refactor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ THEME CONTEXT ============
const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("ftr-theme");
    return saved ? saved === "dark" : true; // Default to dark
  });

  useEffect(() => {
    localStorage.setItem("ftr-theme", isDark ? "dark" : "light");
    document.documentElement.classList.toggle("light-mode", !isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, setIsDark, toggleTheme: () => setIsDark(!isDark) }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => useContext(ThemeContext);

// ============ TRANSLATION CONTEXT ============
// Translations are imported from ./locales/index.js with browser language detection

const LanguageContext = createContext();

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    return { t: (key) => translations["en-US"][key] || key, lang: "en-US", setLang: () => {}, availableLanguages };
  }
  return context;
};

const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    // Check localStorage first
    const savedLang = localStorage.getItem("ftr-lang");
    if (savedLang && translations[savedLang]) {
      return savedLang;
    }
    // Use browser language detection for first-time visitors
    return detectBrowserLanguage();
  });

  // Save language preference and apply to document for RTL support
  useEffect(() => {
    if (lang) {
      localStorage.setItem("ftr-lang", lang);
      // Set document direction for Arabic
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lang;
    }
  }, [lang]);
  
  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations["en-US"][key] || key;
  }, [lang]);

  const updateLang = (newLang) => {
    localStorage.setItem("ftr-lang", newLang);
    setLang(newLang);
  };

  return (
    <LanguageContext.Provider value={{ t, lang, setLang: updateLang, availableLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
};

// ============ SPLASH SCREEN WITH LOGO ANIMATION ============
const SplashScreen = ({ onComplete }) => {
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    // Phase 1: Logo appears and scales (0-1.5s)
    setTimeout(() => setAnimationPhase(1), 100);
    // Phase 2: Text appears (1.5s)
    setTimeout(() => setAnimationPhase(2), 1500);
    // Phase 3: Glow effect (2.5s)
    setTimeout(() => setAnimationPhase(3), 2500);
    // Phase 4: Extra shine (4s)
    setTimeout(() => setAnimationPhase(4), 4000);
    // Complete after 7s
    setTimeout(() => onComplete(), 7000);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-[#09090B] flex items-center justify-center z-[100]" data-testid="splash-screen">
      <div className="text-center">
        {/* Animated Logo Container */}
        <div 
          className={`transition-all duration-1000 ease-out ${
            animationPhase >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
          }`}
        >
          <div className={`relative inline-block ${
            animationPhase >= 3 ? 'animate-pulse' : ''
          }`}>
            {/* Outer glow ring */}
            <div className={`absolute inset-0 -m-8 rounded-full transition-all duration-1000 ${
              animationPhase >= 4 ? 'bg-[#FFD60A]/10 blur-3xl scale-150' : 'opacity-0'
            }`} />
            
            {/* Glow effect behind logo */}
            <div className={`absolute inset-0 blur-2xl transition-opacity duration-500 ${
              animationPhase >= 3 ? 'opacity-50' : 'opacity-0'
            }`}>
              <Car className="w-32 h-32 text-[#FFD60A]" />
            </div>
            
            {/* Main taxi icon */}
            <Car className={`w-32 h-32 text-[#FFD60A] relative z-10 transition-all duration-700 ${
              animationPhase >= 2 ? 'drop-shadow-[0_0_30px_rgba(255,214,10,0.5)]' : ''
            } ${animationPhase >= 4 ? 'drop-shadow-[0_0_50px_rgba(255,214,10,0.7)]' : ''}`} />
          </div>
        </div>
        
        {/* Brand Text */}
        <h1 
          className={`text-5xl md:text-6xl font-black text-white mt-8 tracking-tight transition-all duration-700 ${
            animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Flevo<span className="text-[#FFD60A]">taxi</span>Runner
        </h1>
        
        {/* Tagline */}
        <p className={`text-gray-400 text-lg mt-3 transition-all duration-700 delay-300 ${
          animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          Premium Taxi Service
        </p>
        
        {/* Loading dots */}
        <div className={`flex justify-center gap-2 mt-8 transition-opacity duration-500 ${
          animationPhase >= 2 ? 'opacity-100' : 'opacity-0'
        }`}>
          {[0, 1, 2].map((i) => (
            <div 
              key={i}
              className="w-3 h-3 bg-[#FFD60A] rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
        
        {/* Progress bar */}
        <div className={`w-48 h-1 bg-gray-800 rounded-full mx-auto mt-6 overflow-hidden transition-opacity duration-500 ${
          animationPhase >= 2 ? 'opacity-100' : 'opacity-0'
        }`}>
          <div 
            className="h-full bg-[#FFD60A] rounded-full transition-all ease-linear"
            style={{ 
              width: animationPhase >= 2 ? '100%' : '0%',
              transitionDuration: '5s'
            }}
          />
        </div>
      </div>
    </div>
  );
};

// ============ LANGUAGE SELECTION PAGE ============
const LanguageSelectionPage = () => {
  const navigate = useNavigate();
  const { setLang } = useTranslation();

  const languages = [
    { code: "en-US", name: "English (US)", flag: "🇺🇸" },
    { code: "en-GB", name: "English (UK)", flag: "🇬🇧" },
    { code: "nl", name: "Nederlands", flag: "🇳🇱" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
  ];

  const handleSelectLanguage = (langCode) => {
    setLang(langCode);
    navigate("/home");
  };

  return (
    <div className="min-h-screen bg-[#09090B] relative">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ 
          backgroundImage: 'url(https://images.pexels.com/photos/20302282/pexels-photo-20302282.jpeg)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/50 via-[#09090B]/80 to-[#09090B]" />
      
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        {/* Logo */}
        <div className="mb-12 text-center">
          <Car className="w-20 h-20 text-[#FFD60A] mx-auto mb-4" />
          <h1 className="text-3xl font-black text-white">
            Flevo<span className="text-[#FFD60A]">taxi</span>Runner
          </h1>
        </div>

        {/* Language Selection */}
        <Card className="bg-[#18181B]/90 backdrop-blur-xl border-zinc-800 w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-14 h-14 bg-[#FFD60A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-7 h-7 text-[#FFD60A]" />
            </div>
            <CardTitle className="text-white text-xl">Select Your Language</CardTitle>
            <CardDescription>Choose your preferred language</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            {languages.map((lang) => (
              <Button
                key={lang.code}
                variant="outline"
                className="h-14 bg-zinc-900/50 border-zinc-700 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 text-white justify-start gap-3"
                onClick={() => handleSelectLanguage(lang.code)}
                data-testid={`lang-${lang.code}`}
              >
                <span className="text-2xl">{lang.flag}</span>
                <span className="text-sm">{lang.name}</span>
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============ MAIN LANDING PAGE ============
const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // All prices are INCLUSIVE of 21% BTW (VAT)
  const fixedRoutes = [
    { from: "Almere", to: "Schiphol", price: "79.99", key: "almere_schiphol" },
    { from: "Amsterdam", to: "Schiphol", price: "49.99", key: "amsterdam_schiphol" },
    { from: "Lelystad", to: "Schiphol", price: "109.99", key: "lelystad_schiphol" },
    { from: "Weesp", to: "Schiphol", price: "64.99", key: "weesp_schiphol" },
  ];

  // All EU countries use dynamic pricing ("Offerte op Aanvraag")
  const euTransfers = [
    { country: "Belgium", flag: "🇧🇪", price: "calculated", key: "eu_belgium", isFixed: false },
    { country: "Germany", flag: "🇩🇪", price: "calculated", key: "eu_germany", isFixed: false },
    { country: "France", flag: "🇫🇷", price: "calculated", key: "eu_france", isFixed: false },
    { country: "Other EU Country", flag: "🇪🇺", price: "calculated", key: "eu_other", isFixed: false },
  ];

  const handleRouteClick = (route) => {
    navigate(`/book-route?from=${route.from}&to=${route.to}&price=${route.price}`);
  };

  const handleEUTransferClick = (transfer) => {
    navigate(`/book?euCountry=${transfer.country}&price=${transfer.price}`);
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090B]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/home" className="flex items-center gap-2">
            <Car className="w-8 h-8 text-[#FFD60A]" />
            <span className="text-xl font-bold text-white">Flevo<span className="text-[#FFD60A]">taxi</span>Runner</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors">{t("pricing")}</a>
            <a href="#about" className="text-zinc-400 hover:text-white transition-colors">{t("about")}</a>
            <Link to="/driver" className="text-zinc-400 hover:text-white transition-colors">{t("driverPortal")}</Link>
            <Button 
              onClick={() => navigate('/book')}
              className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full px-6"
              data-testid="nav-book-btn"
            >
              {t("bookNow")}
            </Button>
          </div>

          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-[#18181B] border-t border-white/5 px-6 py-4 space-y-4">
            <a href="#pricing" className="block text-zinc-400 hover:text-white">{t("pricing")}</a>
            <a href="#about" className="block text-zinc-400 hover:text-white">{t("about")}</a>
            <Link to="/driver" className="block text-zinc-400 hover:text-white">{t("driverPortal")}</Link>
            <Button 
              onClick={() => navigate('/book')}
              className="w-full bg-[#FFD60A] text-black font-bold rounded-full"
            >
              {t("bookNow")}
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.pexels.com/photos/20302282/pexels-photo-20302282.jpeg)',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/50 via-[#09090B]/70 to-[#09090B]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <Badge className="mb-6 bg-[#FFD60A]/10 text-[#FFD60A] border-[#FFD60A]/30 px-4 py-1">
            Professional Taxi Service
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-6 tracking-tight animate-fade-in-up">
            {t("yourRide")} <span className="text-[#FFD60A]">{t("anyDestination")}</span><br />{t("madeSimple")}
          </h1>
          
          <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-4 animate-fade-in-up animation-delay-100">
            {t("heroSubtitle")}
          </p>
          <p className="text-base sm:text-lg text-zinc-300 max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-100">
            {t("heroSubtitle2")}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-200">
            <Button 
              onClick={() => navigate('/book')}
              className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full px-10 py-6 text-lg btn-primary-glow"
              data-testid="hero-book-btn"
            >
              {t("bookYourTaxi")} <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline"
              className="border-2 border-zinc-700 text-white hover:bg-zinc-800 rounded-full px-8 py-6 text-lg"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
            >
              {t("viewPricing")}
            </Button>
          </div>
        </div>
      </section>

      {/* Fixed Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t("fixedAirportTransfers")}</h2>
            <p className="text-zinc-400 text-lg">{t("noSurprises")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {fixedRoutes.map((route, index) => (
              <Card 
                key={index}
                className="bg-[#18181B] border-zinc-800 hover:border-[#FFD60A]/50 transition-all duration-300 card-hover cursor-pointer"
                onClick={() => handleRouteClick(route)}
                data-testid={`pricing-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#FFD60A]/10 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#FFD60A]" />
                    </div>
                    <div>
                      <p className="text-zinc-400 text-sm">{t("from")}</p>
                      <p className="text-white font-semibold">{route.from}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#22C55E]/10 flex items-center justify-center">
                      <Navigation className="w-5 h-5 text-[#22C55E]" />
                    </div>
                    <div>
                      <p className="text-zinc-400 text-sm">{t("to")}</p>
                      <p className="text-white font-semibold">{route.to}</p>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    <p className="text-3xl font-black text-[#FFD60A]">€{route.price}</p>
                    <p className="text-zinc-500 text-sm">{t("fixedPrice")}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/book')}
              className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full px-10 py-6 text-lg"
              data-testid="pricing-book-btn"
            >
              {t("bookTransferNow")}
            </Button>
          </div>
        </div>
      </section>

      {/* EU Transfers Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Flevo<span className="text-[#FFD60A]">taxi</span>Runner {t("euTransfersTitle").replace("FlevotaxiRunner ", "")}</h2>
            <p className="text-zinc-400 text-lg">&ldquo;{t("euTransfersQuote")}&rdquo;</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {euTransfers.map((transfer, index) => (
              <Card 
                key={transfer.key}
                className="bg-[#18181B] border-zinc-800 hover:border-[#FFD60A]/50 transition-all duration-300 card-hover cursor-pointer"
                onClick={() => handleEUTransferClick(transfer)}
                data-testid={`eu-transfer-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#FFD60A]/10 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#FFD60A]" />
                    </div>
                    <div>
                      <p className="text-zinc-400 text-sm">{t("from")}</p>
                      <p className="text-white font-semibold">{t("yourPickupLocation")}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#22C55E]/10 flex items-center justify-center text-2xl">
                      {transfer.flag}
                    </div>
                    <div>
                      <p className="text-zinc-400 text-sm">{t("to")}</p>
                      <p className="text-white font-semibold">{transfer.country}</p>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    {transfer.price === "calculated" ? (
                      <>
                        <p className="text-2xl font-black text-[#FFD60A]">{t("customQuote")}</p>
                        <p className="text-zinc-500 text-sm">{t("basedOnDistance")}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-3xl font-black text-[#FFD60A]">€{transfer.price}</p>
                        <p className="text-zinc-500 text-sm">{t("fixedPrice")}</p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <div className="inline-block bg-[#18181B] border border-zinc-700 rounded-xl px-6 py-3">
              <p className="text-zinc-400 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FFD60A]" />
                <span><strong className="text-white">{t("groupTransfers")}</strong> - {t("comingSoon")}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="about" className="py-20 px-6 bg-gradient-to-b from-[#18181B] to-[#09090B]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">{t("whyChoose")}<span className="text-[#FFD60A]">{t("whyChooseTaxi")}</span>{t("whyChooseRunner")}</h2>
              <div className="space-y-6">
                {[
                  { icon: MapPin, title: t("ridesNL"), desc: t("ridesNLDesc") },
                  { icon: Globe, title: t("euTransfers"), desc: t("euTransfersDesc") },
                  { icon: CheckCircle, title: t("fixedPrices"), desc: t("fixedPricesDesc") },
                  { icon: Car, title: t("comfortableVehicles"), desc: t("comfortableVehiclesDesc") },
                ].map((feature, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#FFD60A]/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#FFD60A]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">{feature.title}</h3>
                      <p className="text-zinc-400">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/5834968/pexels-photo-5834968.jpeg"
                alt="Professional Driver"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#FFD60A] text-black p-6 rounded-xl shadow-xl">
                <p className="text-4xl font-black">500+</p>
                <p className="font-semibold">{t("happyCustomers")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Features Section */}
      <section className="py-20 px-6 bg-[#09090B]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t("smartFeatures")}</h2>
            <p className="text-zinc-400 text-lg">{t("smartFeaturesDesc")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* WhatsApp Confirmations */}
            <Card className="bg-[#18181B] border-zinc-800 hover:border-[#25D366]/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center mb-4">
                  <span className="text-2xl">📱</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{t("whatsappConfirm")}</h3>
                <p className="text-zinc-400 text-sm">{t("whatsappConfirmDesc")}</p>
              </CardContent>
            </Card>

            {/* Live Taxi Tracker */}
            <Card className="bg-[#18181B] border-zinc-800 hover:border-[#FFD60A]/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#FFD60A]/10 flex items-center justify-center mb-4">
                  <Navigation className="w-6 h-6 text-[#FFD60A]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{t("liveTaxiTracker")}</h3>
                <p className="text-zinc-400 text-sm">{t("liveTaxiTrackerDesc")}</p>
              </CardContent>
            </Card>

            {/* Direct Driver Messaging */}
            <Card className="bg-[#18181B] border-zinc-800 hover:border-[#3B82F6]/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{t("directDriverMsg")}</h3>
                <p className="text-zinc-400 text-sm">{t("directDriverMsgDesc")}</p>
              </CardContent>
            </Card>

            {/* Email Notifications */}
            <Card className="bg-[#18181B] border-zinc-800 hover:border-[#8B5CF6]/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-[#8B5CF6]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{t("emailNotifications")}</h3>
                <p className="text-zinc-400 text-sm">{t("emailNotificationsDesc")}</p>
              </CardContent>
            </Card>

            {/* Professional Drivers */}
            <Card className="bg-[#18181B] border-zinc-800 hover:border-[#22C55E]/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#22C55E]/10 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-[#22C55E]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{t("professionalDrivers")}</h3>
                <p className="text-zinc-400 text-sm">{t("professionalDriversDesc")}</p>
              </CardContent>
            </Card>

            {/* Secure Payments */}
            <Card className="bg-[#18181B] border-zinc-800 hover:border-[#F59E0B]/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{t("securePayments")}</h3>
                <p className="text-zinc-400 text-sm">{t("securePaymentsDesc")}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <Car className="w-6 h-6 text-[#FFD60A]" />
            <span className="text-lg font-bold text-white">Flevo<span className="text-[#FFD60A]">taxi</span>Runner</span>
          </div>
          <p className="text-zinc-500">{t("footer")}</p>
          <div className="flex gap-6">
            <a href="tel:+31616039911" className="text-zinc-400 hover:text-[#FFD60A] transition-colors">
              <Phone className="w-5 h-5" />
            </a>
            <a href="mailto:info@flevotaxirunner.nl" className="text-zinc-400 hover:text-[#FFD60A] transition-colors">
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </footer>

      <Toaster richColors position="top-right" />
    </div>
  );
};

// ============ FIXED ROUTE BOOKING PAGE ============
const FixedRouteBookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, lang } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [date, setDate] = useState(null);
  
  const fromCity = searchParams.get("from") || "";
  const toCity = searchParams.get("to") || "";
  const fixedPrice = searchParams.get("price") || "";
  
  const [formData, setFormData] = useState({
    pickup_address: "",
    time: "",
    passenger_count: "1",
    extra_info: "",
    suitcases: "",
    customer_name: "",
    phone_prefix: "+31",
    phone_number: "",
    email: ""
  });

  useEffect(() => {
    fetchCountryCodes();
  }, []);

  const fetchCountryCodes = async () => {
    try {
      const response = await axios.get(`${API}/country-codes`);
      setCountryCodes(response.data.country_codes);
    } catch (error) {
      console.error("Failed to fetch country codes:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error(t("selectDate"));
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        pickup_address: formData.pickup_address,
        pickup_city: fromCity,
        destination_address: toCity === "Schiphol" ? "Schiphol Airport" : toCity,
        destination_city: toCity,
        date: format(date, "yyyy-MM-dd"),
        time: formData.time,
        passenger_count: parseInt(formData.passenger_count),
        extra_info: `${formData.extra_info}${formData.suitcases ? ` | Suitcases/Items: ${formData.suitcases}` : ''}`,
        customer_name: formData.customer_name,
        phone_prefix: formData.phone_prefix,
        phone_number: formData.phone_number.replace(/^0+/, ''),
        email: formData.email,
        price: parseFloat(fixedPrice),
        language: lang
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success("Booking created!");
      navigate(`/payment?booking_id=${response.data.booking.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === "phone_number" && value.startsWith("0")) {
      value = value.substring(1);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <nav className="bg-[#09090B]/90 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/home" className="flex items-center gap-2">
            <Car className="w-8 h-8 text-[#FFD60A]" />
            <span className="text-xl font-bold text-white">Flevo<span className="text-[#FFD60A]">taxi</span>Runner</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Route Info Banner */}
        <Card className="bg-[#FFD60A]/10 border-[#FFD60A]/30 mb-8">
          <CardContent className="p-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-zinc-400 text-sm">{t("from")}</p>
                <p className="text-white font-bold text-xl">{fromCity}</p>
              </div>
              <ChevronRight className="w-6 h-6 text-[#FFD60A]" />
              <div className="text-center">
                <p className="text-zinc-400 text-sm">{t("to")}</p>
                <p className="text-white font-bold text-xl">{toCity}</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-zinc-400 text-sm">{t("fixedPrice")}</p>
              <p className="text-[#FFD60A] font-black text-3xl">€{fixedPrice}</p>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Pickup Details */}
          <Card className="bg-[#18181B] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#FFD60A]" /> {t("pickupLocation")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("streetAddress")} *</Label>
                <Input
                  value={formData.pickup_address}
                  onChange={(e) => handleChange("pickup_address", e.target.value)}
                  placeholder="e.g., Stationsplein 1"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="pickup-address-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Date & Time */}
          <Card className="bg-[#18181B] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#FFD60A]" /> {t("dateTime")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("date")} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-zinc-950/50 border-zinc-800 h-12 text-white"
                      data-testid="date-picker-btn"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : <span className="text-zinc-500">{t("selectDate")}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#18181B] border-zinc-800">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("time")} *</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleChange("time", e.target.value)}
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="time-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("passengers")} *</Label>
                <Select value={formData.passenger_count} onValueChange={(v) => handleChange("passenger_count", v)}>
                  <SelectTrigger className="bg-zinc-950/50 border-zinc-800 h-12 text-white" data-testid="passengers-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-zinc-800">
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? t("passenger") : t("passengers")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Customer Details */}
          <Card className="bg-[#18181B] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FFD60A]" /> {t("yourDetails")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("fullName")} *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => handleChange("customer_name", e.target.value)}
                  placeholder="John Doe"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="customer-name-input"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("countryCode")} *</Label>
                  <Select value={formData.phone_prefix} onValueChange={(v) => handleChange("phone_prefix", v)}>
                    <SelectTrigger className="bg-zinc-950/50 border-zinc-800 h-12 text-white" data-testid="phone-prefix-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-zinc-800 max-h-[300px]">
                      {countryCodes.map(cc => (
                        <SelectItem key={cc.iso} value={cc.code}>
                          {cc.code} ({cc.country})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("phoneNumber")} *</Label>
                  <Input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => handleChange("phone_number", e.target.value)}
                    placeholder="612345678"
                    className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                    required
                    data-testid="phone-number-input"
                  />
                  <p className="text-zinc-500 text-xs mt-1">{t("phoneHint")}</p>
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("email")} *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="john@example.com"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("suitcases")}</Label>
                <Input
                  value={formData.suitcases}
                  onChange={(e) => handleChange("suitcases", e.target.value)}
                  placeholder={t("suitcasesPlaceholder")}
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  data-testid="suitcases-input"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("extraInfo")}</Label>
                <Textarea
                  value={formData.extra_info}
                  onChange={(e) => handleChange("extra_info", e.target.value)}
                  placeholder={t("extraInfoPlaceholder")}
                  className="bg-zinc-950/50 border-zinc-800 text-white min-h-[100px]"
                  data-testid="extra-info-textarea"
                />
              </div>
            </CardContent>
          </Card>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full py-6 text-lg btn-primary-glow"
            data-testid="submit-booking-btn"
          >
            {loading ? t("creatingBooking") : `${t("bookMyTaxiRide")} - €${fixedPrice}`}
          </Button>
        </form>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
};

// ============ GENERAL BOOKING PAGE ============
const BookingPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const [searchParams] = useSearchParams();
  const euCountry = searchParams.get("euCountry");
  const euPrice = searchParams.get("price");
  const [loading, setLoading] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [date, setDate] = useState(null);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  
  // All EU countries use dynamic pricing now ("Offerte op Aanvraag")
  // No fixed prices for EU countries
  const euFixedPrices = {};
  
  // Dynamic pricing for all routes (including EU)
  // Base rates (excl. BTW): €1.157 base + €1.074/km + €0.297/min
  // Final price = result * 1.21 (21% BTW)
  const getEuDynamicPrice = (distanceKm, durationMin) => {
    const base = 1.157;
    const perKm = 1.074;
    const perMin = 0.297;
    return base + (distanceKm * perKm) + (durationMin * perMin);
  };
  
  const [formData, setFormData] = useState({
    pickup_address: "",
    pickup_city: "",
    destination_address: "",
    destination_city: euCountry || "",
    time: "",
    passenger_count: "1",
    extra_info: "",
    suitcases: "",
    customer_name: "",
    phone_prefix: "+31",
    phone_number: "",
    email: ""
  });

  useEffect(() => {
    fetchCountryCodes();
  }, []);

  const fetchCountryCodes = async () => {
    try {
      const response = await axios.get(`${API}/country-codes`);
      setCountryCodes(response.data.country_codes);
    } catch (error) {
      console.error("Failed to fetch country codes:", error);
    }
  };

  const calculateDistance = useCallback(async () => {
    if (!formData.pickup_address || !formData.pickup_city || !formData.destination_address || !formData.destination_city) {
      return;
    }

    setCalculatingDistance(true);
    try {
      const origin = `${formData.pickup_address}, ${formData.pickup_city}`;
      const destination = `${formData.destination_address}, ${formData.destination_city}`;
      
      const response = await axios.post(`${API}/calculate-distance`, { origin, destination });
      setDistanceInfo(response.data);
    } catch (error) {
      console.error("Distance calculation error:", error);
    } finally {
      setCalculatingDistance(false);
    }
  }, [formData.pickup_address, formData.pickup_city, formData.destination_address, formData.destination_city]);

  useEffect(() => {
    const timer = setTimeout(calculateDistance, 1000);
    return () => clearTimeout(timer);
  }, [calculateDistance]);

  const calculatePrice = () => {
    const pickupLower = formData.pickup_city.toLowerCase();
    const destLower = formData.destination_city.toLowerCase();
    
    // BTW rate for customers (21%)
    const BTW_RATE = 1.21;

    // Fixed route prices (domestic) - prices INCLUDE 21% BTW
    const routes = {
      almere: { schiphol: 79.99 },
      amsterdam: { schiphol: 49.99 },
      lelystad: { schiphol: 109.99 },
      weesp: { schiphol: 64.99 }
    };

    for (const [from, destinations] of Object.entries(routes)) {
      for (const [to, price] of Object.entries(destinations)) {
        if ((pickupLower.includes(from) && destLower.includes(to)) ||
            (pickupLower.includes(to) && destLower.includes(from))) {
          return price.toFixed(2);
        }
      }
    }

    // Dynamic pricing - Base rates (excl. BTW): €1.157 base + €1.074/km + €0.297/min
    // Final price = result * 1.21 (21% BTW)
    const baseFare = 1.157;
    const perKmRate = 1.074;
    const perMinuteRate = 0.297;

    if (distanceInfo?.distance_km) {
      let priceExclBtw = baseFare + (distanceInfo.distance_km * perKmRate);
      
      // Add time-based cost if duration is available
      if (distanceInfo.duration_min && distanceInfo.duration_min > 0) {
        priceExclBtw += distanceInfo.duration_min * perMinuteRate;
      }
      
      // Add 21% BTW to show customer the total price
      const priceInclBtw = priceExclBtw * BTW_RATE;
      return priceInclBtw.toFixed(2);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error(t("selectDate"));
      return;
    }

    setLoading(true);
    try {
      const price = calculatePrice();
      const bookingData = {
        ...formData,
        phone_number: formData.phone_number.replace(/^0+/, ''),
        extra_info: `${formData.extra_info}${formData.suitcases ? ` | Suitcases/Items: ${formData.suitcases}` : ''}`,
        date: format(date, "yyyy-MM-dd"),
        passenger_count: parseInt(formData.passenger_count),
        distance_km: distanceInfo?.distance_km,
        duration_min: distanceInfo?.duration_min,
        price: price ? parseFloat(price) : null,
        language: lang
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success("Booking created!");
      navigate(`/payment?booking_id=${response.data.booking.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === "phone_number" && value.startsWith("0")) {
      value = value.substring(1);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const price = calculatePrice();

  return (
    <div className="min-h-screen bg-[#09090B]">
      <nav className="bg-[#09090B]/90 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/home" className="flex items-center gap-2">
            <Car className="w-8 h-8 text-[#FFD60A]" />
            <span className="text-xl font-bold text-white">Flevo<span className="text-[#FFD60A]">taxi</span>Runner</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t("bookYourTaxiTitle")}</h1>
          <p className="text-zinc-400">{t("fillDetails")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Pickup Section */}
          <Card className="bg-[#18181B] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#FFD60A]" /> {t("pickupLocation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("streetAddress")} *</Label>
                <Input
                  value={formData.pickup_address}
                  onChange={(e) => handleChange("pickup_address", e.target.value)}
                  placeholder="e.g., Stationsplein 1"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="pickup-address-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("city")} * <span className="text-[#FFD60A]">(Only NL Cities)</span></Label>
                <Input
                  value={formData.pickup_city}
                  onChange={(e) => handleChange("pickup_city", e.target.value)}
                  placeholder="e.g., Almere, Amsterdam, Utrecht"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="pickup-city-input"
                  list="nl-cities-pickup"
                />
                <datalist id="nl-cities-pickup">
                  <option value="Almere" />
                  <option value="Amsterdam" />
                  <option value="Rotterdam" />
                  <option value="Den Haag" />
                  <option value="Utrecht" />
                  <option value="Eindhoven" />
                  <option value="Tilburg" />
                  <option value="Groningen" />
                  <option value="Breda" />
                  <option value="Nijmegen" />
                  <option value="Apeldoorn" />
                  <option value="Haarlem" />
                  <option value="Arnhem" />
                  <option value="Enschede" />
                  <option value="Amersfoort" />
                  <option value="Zaanstad" />
                  <option value="Haarlemmermeer" />
                  <option value="Schiphol" />
                  <option value="Lelystad" />
                  <option value="Weesp" />
                  <option value="Hilversum" />
                  <option value="Zwolle" />
                  <option value="Leiden" />
                  <option value="Dordrecht" />
                  <option value="Maastricht" />
                </datalist>
              </div>
            </CardContent>
          </Card>

          {/* Destination Section */}
          <Card className="bg-[#18181B] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Navigation className="w-5 h-5 text-[#22C55E]" /> {t("destination")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("streetAddress")} *</Label>
                <Input
                  value={formData.destination_address}
                  onChange={(e) => handleChange("destination_address", e.target.value)}
                  placeholder="e.g., Schiphol Airport"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="destination-address-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("city")} * <span className="text-[#FFD60A]">(Only NL Cities)</span></Label>
                <Input
                  value={formData.destination_city}
                  onChange={(e) => handleChange("destination_city", e.target.value)}
                  placeholder="e.g., Schiphol, Amsterdam"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="destination-city-input"
                  list="nl-cities-destination"
                />
                <datalist id="nl-cities-destination">
                  <option value="Almere" />
                  <option value="Amsterdam" />
                  <option value="Rotterdam" />
                  <option value="Den Haag" />
                  <option value="Utrecht" />
                  <option value="Eindhoven" />
                  <option value="Tilburg" />
                  <option value="Groningen" />
                  <option value="Breda" />
                  <option value="Nijmegen" />
                  <option value="Apeldoorn" />
                  <option value="Haarlem" />
                  <option value="Arnhem" />
                  <option value="Enschede" />
                  <option value="Amersfoort" />
                  <option value="Zaanstad" />
                  <option value="Haarlemmermeer" />
                  <option value="Schiphol" />
                  <option value="Lelystad" />
                  <option value="Weesp" />
                  <option value="Hilversum" />
                  <option value="Zwolle" />
                  <option value="Leiden" />
                  <option value="Dordrecht" />
                  <option value="Maastricht" />
                </datalist>
              </div>
            </CardContent>
          </Card>

          {/* Distance Info */}
          {distanceInfo && (
            <Card className="bg-zinc-900/50 border-[#FFD60A]/30">
              <CardContent className="p-4 flex justify-between items-center">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-zinc-400 text-sm">{t("distance")}</p>
                    <p className="text-white font-bold">{distanceInfo.distance_km?.toFixed(1)} km</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">{t("estDuration")}</p>
                    <p className="text-white font-bold">{distanceInfo.duration_min} min</p>
                  </div>
                </div>
                {price && (
                  <div className="text-right">
                    <p className="text-zinc-400 text-sm">{t("price")}</p>
                    <p className="text-[#FFD60A] font-black text-2xl">€{price}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {calculatingDistance && (
            <p className="text-zinc-500 text-center">Calculating distance...</p>
          )}

          {/* Date & Time Section */}
          <Card className="bg-[#18181B] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#FFD60A]" /> {t("dateTime")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("date")} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-zinc-950/50 border-zinc-800 h-12 text-white"
                      data-testid="date-picker-btn"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : <span className="text-zinc-500">{t("selectDate")}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#18181B] border-zinc-800">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("time")} *</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleChange("time", e.target.value)}
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="time-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("passengers")} *</Label>
                <Select value={formData.passenger_count} onValueChange={(v) => handleChange("passenger_count", v)}>
                  <SelectTrigger className="bg-zinc-950/50 border-zinc-800 h-12 text-white" data-testid="passengers-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-zinc-800">
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? t("passenger") : t("passengers")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Customer Details */}
          <Card className="bg-[#18181B] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FFD60A]" /> {t("yourDetails")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("fullName")} *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => handleChange("customer_name", e.target.value)}
                  placeholder="John Doe"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="customer-name-input"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("countryCode")} *</Label>
                  <Select value={formData.phone_prefix} onValueChange={(v) => handleChange("phone_prefix", v)}>
                    <SelectTrigger className="bg-zinc-950/50 border-zinc-800 h-12 text-white" data-testid="phone-prefix-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-zinc-800 max-h-[300px]">
                      {countryCodes.map(cc => (
                        <SelectItem key={cc.iso} value={cc.code}>
                          {cc.code} ({cc.country})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("phoneNumber")} *</Label>
                  <Input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => handleChange("phone_number", e.target.value)}
                    placeholder="612345678"
                    className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                    required
                    data-testid="phone-number-input"
                  />
                  <p className="text-zinc-500 text-xs mt-1">{t("phoneHint")}</p>
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("email")} *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="john@example.com"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("suitcases")}</Label>
                <Input
                  value={formData.suitcases}
                  onChange={(e) => handleChange("suitcases", e.target.value)}
                  placeholder={t("suitcasesPlaceholder")}
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  data-testid="suitcases-input"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-sm uppercase tracking-wider mb-2 block">{t("extraInfo")}</Label>
                <Textarea
                  value={formData.extra_info}
                  onChange={(e) => handleChange("extra_info", e.target.value)}
                  placeholder={t("extraInfoPlaceholder")}
                  className="bg-zinc-950/50 border-zinc-800 text-white min-h-[100px]"
                  data-testid="extra-info-textarea"
                />
              </div>
            </CardContent>
          </Card>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full py-6 text-lg btn-primary-glow"
            data-testid="submit-booking-btn"
          >
            {loading ? t("creatingBooking") : `${t("bookMyTaxiRide")} ${price ? `- €${price}` : ''}`}
          </Button>
        </form>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
};

// ============ PAYMENT PAGE ============
const PaymentPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card"); // "card" or "cash"
  const [finalPrice, setFinalPrice] = useState(null);
  const [waitingTermsAccepted, setWaitingTermsAccepted] = useState(false);
  
  // Referral code state
  const [referralCode, setReferralCode] = useState("");
  const [referralApplied, setReferralApplied] = useState(null);
  const [validatingReferral, setValidatingReferral] = useState(false);

  const fetchBooking = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data.booking);
      setFinalPrice(response.data.booking.price);
      
      // Check if customer has a referral discount
      if (response.data.booking.phone_number) {
        try {
          const referralCheck = await axios.get(`${API}/referral/check-discount?phone=${response.data.booking.phone_prefix}${response.data.booking.phone_number}`);
          if (referralCheck.data.has_discount) {
            setReferralApplied({
              percentage: referralCheck.data.discount_percentage,
              code: referralCheck.data.referral_code,
              autoApplied: true
            });
            const discount = response.data.booking.price * (referralCheck.data.discount_percentage / 100);
            setFinalPrice(response.data.booking.price - discount);
          }
        } catch (e) {
          // No referral discount available
        }
      }
    } catch (error) {
      toast.error("Failed to load booking");
      navigate("/home");
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  useEffect(() => {
    if (bookingId) {
      fetchBooking();
    }
  }, [bookingId, fetchBooking]);

  // Validate and apply referral code
  const validateReferralCode = async () => {
    if (!referralCode || referralCode.length < 6) {
      toast.error("Voer een geldige verwijzingscode in");
      return;
    }

    setValidatingReferral(true);
    try {
      const response = await axios.post(`${API}/referral/apply`, {
        code: referralCode,
        new_customer_phone: `${booking.phone_prefix}${booking.phone_number}`,
        new_customer_email: booking.email
      });
      
      if (response.data.discount_percentage) {
        const discountPercent = response.data.discount_percentage;
        const discountAmount = booking.price * (discountPercent / 100);
        setReferralApplied({
          percentage: discountPercent,
          amount: discountAmount.toFixed(2),
          code: referralCode.toUpperCase()
        });
        setFinalPrice(booking.price - discountAmount);
        toast.success(`${discountPercent}% ${t("discountAppliedPercent")}!`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t("invalidReferralCode");
      toast.error(errorMsg);
    } finally {
      setValidatingReferral(false);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode || discountCode.length !== 6) {
      toast.error(t("enterValid6DigitCode"));
      return;
    }

    if (booking.price < 30) {
      toast.error(t("minBookingForDiscount"));
      return;
    }

    setValidatingCode(true);
    try {
      const response = await axios.get(`${API}/discount-codes/validate/${discountCode}`);
      if (response.data.valid) {
        const discount = booking.price * 0.10;
        const newPrice = (booking.price - discount).toFixed(2);
        setDiscountApplied({
          code: discountCode,
          percentage: 10,
          amount: discount.toFixed(2)
        });
        setFinalPrice(parseFloat(newPrice));
        toast.success(`10% ${t("discountAppliedPercent")}! ${t("youSave")} €${discount.toFixed(2)}`);
      } else {
        toast.error(response.data.message || t("invalidDiscountCode"));
      }
    } catch (error) {
      toast.error(t("invalidDiscountCode"));
    } finally {
      setValidatingCode(false);
    }
  };

  const removeDiscount = () => {
    setDiscountApplied(null);
    setDiscountCode("");
    setFinalPrice(booking.price);
    toast.info(t("discountRemoved"));
  };

  const handleCardPayment = async () => {
    setProcessing(true);
    try {
      // Update booking with discount if applied
      if (discountApplied) {
        await axios.put(`${API}/bookings/${bookingId}/apply-discount`, {
          discount_code: discountApplied.code,
          original_price: booking.price,
          final_price: finalPrice,
          discount_amount: parseFloat(discountApplied.amount)
        });
      }

      const response = await axios.post(`${API}/payments/create-checkout`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate payment");
      setProcessing(false);
    }
  };

  const handleCashPayment = async () => {
    setProcessing(true);
    try {
      // Update booking with cash payment method and discount if applied
      await axios.post(`${API}/bookings/${bookingId}/pay-cash`, {
        discount_code: discountApplied?.code || null,
        original_price: booking.price,
        final_price: finalPrice,
        discount_amount: discountApplied ? parseFloat(discountApplied.amount) : 0
      });
      
      toast.success("Cash booking confirmed! Driver has been notified.");
      navigate(`/booking-success?booking_id=${bookingId}&payment=cash`);
    } catch (error) {
      toast.error("Failed to process cash booking");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FFD60A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      <nav className="bg-[#09090B]/90 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/home" className="flex items-center gap-2">
            <Car className="w-8 h-8 text-[#FFD60A]" />
            <span className="text-xl font-bold text-white">Flevo<span className="text-[#FFD60A]">taxi</span>Runner</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-white mb-4">{t("paymentTitle")}</h1>
          <p className="text-zinc-400">{t("paymentSubtitle")}</p>
        </div>

        {booking && (
          <>
            {/* Booking Summary */}
            <Card className="bg-[#18181B] border-zinc-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white">{t("bookingSummary")}</CardTitle>
                <CardDescription className="font-mono text-zinc-500">ID: {booking.id}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-500 text-sm">{t("from")}</p>
                    <p className="text-white">{booking.pickup_address}, {booking.pickup_city}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">{t("to")}</p>
                    <p className="text-white">{booking.destination_address}, {booking.destination_city}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">{t("dateTime")}</p>
                    <p className="text-white">{booking.date} at {booking.time}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">{t("passengers")}</p>
                    <p className="text-white">{booking.passenger_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Referral Code Section - 15% discount for new customers */}
            {!referralApplied && !discountApplied && (
              <Card className="bg-[#18181B] border-zinc-800 mb-6">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <span>🤝</span> Verwijzingscode
                  </CardTitle>
                  <CardDescription className="text-zinc-500">
                    Heb je een verwijzingscode van een vriend? Ontvang 15% korting op je eerste rit!
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder={t("referralCodePlaceholder")}
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      maxLength={12}
                      className="bg-zinc-900 border-zinc-700 text-white font-mono text-lg tracking-widest"
                    />
                    <Button
                      onClick={validateReferralCode}
                      disabled={validatingReferral || referralCode.length < 6}
                      className="bg-[#22C55E] text-white font-bold hover:bg-[#16A34A]"
                    >
                      {validatingReferral ? "..." : t("apply")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Referral Discount Applied */}
            {referralApplied && (
              <Card className="bg-green-900/20 border-green-500 mb-6">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-green-400 font-bold">🎉 {t("referralDiscountApplied")}</p>
                      <p className="text-green-300 text-sm">
                        {referralApplied.autoApplied ? t("autoApplied") : `Code: ${referralApplied.code}`} • {referralApplied.percentage}% {t("discountAppliedPercent")}
                      </p>
                    </div>
                    <span className="text-green-400 font-bold text-xl">-€{(booking.price * (referralApplied.percentage / 100)).toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Discount Code Section */}
            {!referralApplied && (
            <Card className="bg-[#18181B] border-zinc-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <span>🎟️</span> {t("discountCode")}
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  {t("discountCodeHint")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!discountApplied ? (
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder={t("enterDiscountCode")}
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="bg-zinc-900 border-zinc-700 text-white font-mono text-lg tracking-widest"
                    />
                    <Button
                      onClick={validateDiscountCode}
                      disabled={validatingCode || discountCode.length !== 6}
                      className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308]"
                    >
                      {validatingCode ? "..." : t("apply")}
                    </Button>
                  </div>
                ) : (
                  <div className="bg-green-900/30 border border-green-500 rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <p className="text-green-400 font-bold">✅ {t("discountApplied")}</p>
                      <p className="text-green-300 text-sm">Code: {discountApplied.code} • {discountApplied.percentage}% {t("discountAppliedPercent")} • -€{discountApplied.amount}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeDiscount}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                    >
                      {t("remove")}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Price Summary */}
            <Card className="bg-[#18181B] border-zinc-800 mb-6">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-zinc-400">
                    <span>{t("subtotal")} (incl. 21% BTW)</span>
                    <span>€{booking.price}</span>
                  </div>
                  {discountApplied && (
                    <div className="flex justify-between items-center text-green-400">
                      <span>{t("discountCode")} ({discountApplied.percentage}%)</span>
                      <span>-€{discountApplied.amount}</span>
                    </div>
                  )}
                  {referralApplied && (
                    <div className="flex justify-between items-center text-green-400">
                      <span>{t("referralDiscount")} ({referralApplied.percentage}%)</span>
                      <span>-€{(booking.price * (referralApplied.percentage / 100)).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-zinc-700 pt-3 flex justify-between items-center">
                    <span className="text-white font-bold text-lg">{t("totalAmount")}</span>
                    <span className="text-[#FFD60A] font-black text-3xl">€{finalPrice?.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Selection */}
            <Card className="bg-[#18181B] border-zinc-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white">{t("choosePaymentMethod")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Card Payment Option */}
                <div 
                  onClick={() => setPaymentMethod("card")}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    paymentMethod === "card" 
                      ? "border-[#FFD60A] bg-[#FFD60A]/10" 
                      : "border-zinc-700 hover:border-zinc-500"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === "card" ? "border-[#FFD60A]" : "border-zinc-500"
                    }`}>
                      {paymentMethod === "card" && <div className="w-3 h-3 rounded-full bg-[#FFD60A]" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-bold flex items-center gap-2">
                        💳 {t("payOnline")}
                      </p>
                      <p className="text-zinc-400 text-sm">{t("securePayment")}</p>
                    </div>
                  </div>
                </div>

                {/* Cash Payment Option */}
                <div 
                  onClick={() => setPaymentMethod("cash")}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    paymentMethod === "cash" 
                      ? "border-green-500 bg-green-500/10" 
                      : "border-zinc-700 hover:border-zinc-500"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === "cash" ? "border-green-500" : "border-zinc-500"
                    }`}>
                      {paymentMethod === "cash" && <div className="w-3 h-3 rounded-full bg-green-500" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-bold flex items-center gap-2">
                        💶 {t("payCash")}
                      </p>
                      <p className="text-zinc-400 text-sm">{t("payCashDesc")} €{finalPrice?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Waiting Time Terms - Mandatory Checkbox */}
            <Card className="bg-[#18181B] border-zinc-800 mb-6">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="waiting-terms"
                    checked={waitingTermsAccepted}
                    onChange={(e) => setWaitingTermsAccepted(e.target.checked)}
                    className="w-5 h-5 mt-1 rounded border-zinc-600 bg-zinc-900 text-[#FFD60A] focus:ring-[#FFD60A] cursor-pointer"
                  />
                  <label htmlFor="waiting-terms" className="text-zinc-300 text-sm cursor-pointer">
                    <span className="font-bold text-white">⚠️ {t("importantTerms")}</span>
                    <br />
                    <span className="text-zinc-400">
                      {t("waitingTimeTerms")}
                    </span>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Payment Button */}
            <div className="space-y-4">
              {paymentMethod === "card" ? (
                <Button
                  onClick={handleCardPayment}
                  disabled={processing || !waitingTermsAccepted}
                  className={`w-full font-bold rounded-full py-6 text-lg ${
                    waitingTermsAccepted 
                      ? "bg-[#FFD60A] text-black hover:bg-[#EAB308] btn-primary-glow" 
                      : "bg-zinc-700 text-zinc-400 cursor-not-allowed"
                  }`}
                  data-testid="pay-now-btn"
                >
                  {processing ? t("processing") : `💳 ${t("payNow")} - €${finalPrice?.toFixed(2)}`}
                </Button>
              ) : (
                <Button
                  onClick={handleCashPayment}
                  disabled={processing || !waitingTermsAccepted}
                  className={`w-full font-bold rounded-full py-6 text-lg ${
                    waitingTermsAccepted 
                      ? "bg-green-600 text-white hover:bg-green-500" 
                      : "bg-zinc-700 text-zinc-400 cursor-not-allowed"
                  }`}
                  data-testid="pay-cash-btn"
                >
                  {processing ? t("processing") : `💶 ${t("payCash")} - €${finalPrice?.toFixed(2)}`}
                </Button>
              )}
              
              {!waitingTermsAccepted && (
                <p className="text-center text-amber-500 text-sm">
                  ⚠️ {t("acceptTermsToProceeed")}
                </p>
              )}
              
              <p className="text-center text-zinc-500 text-sm">
                {paymentMethod === "card" ? t("securePayment") : t("payCashDesc")}
              </p>
            </div>
          </>
        )}
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
};

// ============ BOOKING SUCCESS PAGE ============
const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");
  const paymentType = searchParams.get("payment"); // "cash" for cash payments
  const [status, setStatus] = useState(paymentType === "cash" ? "success" : "checking");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!bookingId) return;
    
    const fetchBooking = async () => {
      try {
        const response = await axios.get(`${API}/bookings/${bookingId}`);
        setBooking(response.data.booking);
      } catch (error) {
        console.error("Failed to fetch booking");
      }
    };
    
    fetchBooking();
  }, [bookingId]);

  useEffect(() => {
    // Skip payment polling for cash payments
    if (!sessionId || paymentType === "cash") return;
    
    let timeoutId;
    let isCancelled = false;
    
    const pollPaymentStatus = async (attempts = 0) => {
      if (isCancelled) return;
      if (attempts >= 10) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        if (isCancelled) return;
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          // Trigger WhatsApp notification to chauffeur after successful payment
          await axios.post(`${API}/bookings/${bookingId}/notify-payment`);
          return;
        } else if (response.data.status === "expired") {
          setStatus("expired");
          return;
        }
        
        timeoutId = setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      } catch (error) {
        if (!isCancelled) setStatus("error");
      }
    };

    pollPaymentStatus();

    return () => {
      isCancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, bookingId, paymentType]);

  const isCashPayment = paymentType === "cash" || booking?.payment_method === "cash";

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
      <Card className="bg-[#18181B] border-zinc-800 max-w-lg w-full text-center">
        <CardContent className="p-10">
          {status === "checking" && (
            <>
              <div className="animate-spin w-16 h-16 border-4 border-[#FFD60A] border-t-transparent rounded-full mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-white mb-2">{t("processingPayment")}</h2>
              <p className="text-zinc-400">{t("pleaseWait")}</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className={`w-20 h-20 ${isCashPayment ? 'bg-green-500/20' : 'bg-[#22C55E]/20'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                <CheckCircle className={`w-10 h-10 ${isCashPayment ? 'text-green-500' : 'text-[#22C55E]'}`} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{t("bookingConfirmed")}</h2>
              <p className="text-zinc-400 mb-6">{t("bookingConfirmedMsg")}</p>
              
              {isCashPayment ? (
                <div className="bg-green-900/30 border border-green-500 rounded-xl p-4 mb-6">
                  <p className="text-green-400 font-bold flex items-center justify-center gap-2">
                    💶 {t("cashPaymentSelected")}
                  </p>
                  <p className="text-green-300 text-sm mt-2">
                    {t("cashPaymentHint").replace("{amount}", booking?.price?.toFixed(2) || "0.00")}
                  </p>
                </div>
              ) : (
                <div className="bg-[#FFD60A]/10 border border-[#FFD60A] rounded-xl p-4 mb-6">
                  <p className="text-[#FFD60A] font-bold flex items-center justify-center gap-2">
                    💳 {t("cardPaymentSelected")}
                  </p>
                  <p className="text-[#FFD60A]/80 text-sm mt-2">
                    {t("cardPaymentHint").replace("{amount}", booking?.price?.toFixed(2) || "0.00")}
                  </p>
                </div>
              )}
              
              {booking && (
                <div className="bg-zinc-900 rounded-xl p-4 mb-6 text-left">
                  <p className="text-zinc-500 text-sm mb-1">{t("bookingId")}</p>
                  <p className="text-white font-mono text-sm mb-3">{booking.id}</p>
                  <p className="text-zinc-500 text-sm mb-1">{t("pickup")}</p>
                  <p className="text-white mb-3">{booking.pickup_address}, {booking.pickup_city}</p>
                  <p className="text-zinc-500 text-sm mb-1">{t("dateTime")}</p>
                  <p className="text-white mb-3">{booking.date} at {booking.time}</p>
                  {booking.discount_applied > 0 && (
                    <>
                      <p className="text-zinc-500 text-sm mb-1">Discount Applied</p>
                      <p className="text-green-400 mb-3">-€{booking.discount_applied?.toFixed(2)} ({booking.discount_code_used})</p>
                    </>
                  )}
                  <p className="text-zinc-500 text-sm mb-1">Total</p>
                  <p className="text-[#FFD60A] font-bold text-xl">€{booking.price?.toFixed(2)}</p>
                </div>
              )}

              <Button
                onClick={() => navigate(`/track/${bookingId}`)}
                className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full"
                data-testid="track-taxi-btn"
              >
                {t("trackYourTaxi")}
              </Button>
            </>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <>
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{t("paymentIssue")}</h2>
              <p className="text-zinc-400 mb-6">{t("paymentIssueMsg")}</p>
              <Button
                onClick={() => navigate('/book')}
                className="w-full bg-zinc-800 text-white hover:bg-zinc-700 rounded-full"
              >
                {t("tryAgain")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ TRACKING PAGE ============
const TrackingPage = () => {
  const { bookingId } = useParams();
  const { t } = useTranslation();
  const [tracking, setTracking] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTracking = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/track`);
      setBooking(response.data.booking);
      setTracking(response.data.tracking);
    } catch (error) {
      console.error("Failed to fetch tracking");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchTracking();
    const interval = setInterval(fetchTracking, 10000);
    return () => clearInterval(interval);
  }, [fetchTracking]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FFD60A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      <nav className="bg-[#09090B]/90 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/home" className="flex items-center gap-2">
            <Car className="w-8 h-8 text-[#FFD60A]" />
            <span className="text-xl font-bold text-white">Flevo<span className="text-[#FFD60A]">taxi</span>Runner</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-white mb-8">{t("trackYourTaxi")}</h1>

        {booking && (
          <Card className="bg-[#18181B] border-zinc-800 mb-6">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <Badge className={`status-${booking.status}`}>
                    {booking.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
                <p className="text-zinc-500 font-mono text-sm">{booking.id.slice(0, 8)}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-zinc-500 text-sm mb-1">{t("pickup")}</p>
                  <p className="text-white">{booking.pickup_address}, {booking.pickup_city}</p>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm mb-1">{t("destination")}</p>
                  <p className="text-white">{booking.destination_address}, {booking.destination_city}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {tracking ? (
          <Card className="bg-[#18181B] border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-white font-semibold text-lg">{t("driverLocation")}</h3>
                  <p className="text-zinc-400 text-sm">{t("liveTracking")}</p>
                </div>
                <div className="relative">
                  <div className="w-4 h-4 bg-[#22C55E] rounded-full"></div>
                  <div className="absolute inset-0 w-4 h-4 bg-[#22C55E] rounded-full pulse-ring"></div>
                </div>
              </div>

              {tracking.distance_km && tracking.eta_minutes && (
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-zinc-900 rounded-xl p-4 text-center">
                    <p className="text-zinc-400 text-sm mb-1">{t("distance")}</p>
                    <p className="text-[#FFD60A] font-black text-3xl">{tracking.distance_km}</p>
                    <p className="text-zinc-500">{t("kilometers")}</p>
                  </div>
                  <div className="bg-zinc-900 rounded-xl p-4 text-center">
                    <p className="text-zinc-400 text-sm mb-1">{t("eta")}</p>
                    <p className="text-[#FFD60A] font-black text-3xl">{tracking.eta_minutes}</p>
                    <p className="text-zinc-500">{t("minutes")}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#18181B] border-zinc-800">
            <CardContent className="p-6 text-center">
              <Car className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">{t("waitingDriver")}</p>
              <p className="text-zinc-500 text-sm mt-2">{t("waitingDriverHint")}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============ DRIVER DASHBOARD ============
const DriverDashboard = () => {
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pin, setPin] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [driver, setDriver] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [lastBookingCount, setLastBookingCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  
  // PIN management states
  const [requires2FA, setRequires2FA] = useState(false);
  const [needs2FASetup, setNeeds2FASetup] = useState(false);  // Shows "2FA Instellen" button
  const [pinValidated, setPinValidated] = useState(false);     // PIN has been validated
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [showResetPinModal, setShowResetPinModal] = useState(false);
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [qrCode2FA, setQrCode2FA] = useState("");
  const [secret2FA, setSecret2FA] = useState("");
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmNewPin, setConfirmNewPin] = useState("");
  const [changePinTotp, setChangePinTotp] = useState("");
  const [pinType, setPinType] = useState("");
  
  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState("month");
  const [exportMonth, setExportMonth] = useState("");
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [availablePeriods, setAvailablePeriods] = useState({ months: [], years: [] });
  const [exportLoading, setExportLoading] = useState(false);

  // Accountant states
  const [showAccountantModal, setShowAccountantModal] = useState(false);
  const [accountantSettings, setAccountantSettings] = useState({
    name: "", email: "", company_name: "", phone: "", notes: ""
  });
  const [accountantLoading, setAccountantLoading] = useState(false);

  // Fetch accountant settings
  const fetchAccountantSettings = async () => {
    try {
      const response = await axios.get(`${API}/accountant/settings`);
      if (response.data.configured) {
        setAccountantSettings(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch accountant settings");
    }
  };

  // Save accountant settings
  const handleSaveAccountant = async () => {
    if (!accountantSettings.name || !accountantSettings.email) {
      toast.error("Naam en email zijn verplicht");
      return;
    }
    setAccountantLoading(true);
    try {
      await axios.post(`${API}/accountant/settings`, accountantSettings);
      toast.success("Accountant gegevens opgeslagen!");
      setShowAccountantModal(false);
    } catch (error) {
      toast.error("Fout bij opslaan: " + (error.response?.data?.detail || error.message));
    } finally {
      setAccountantLoading(false);
    }
  };

  // Send report to accountant
  const handleSendAccountantReport = async () => {
    if (!accountantSettings.email) {
      toast.error("Configureer eerst accountant email");
      setShowAccountantModal(true);
      return;
    }
    setAccountantLoading(true);
    try {
      const response = await axios.post(`${API}/accountant/send-report`);
      toast.success(`Rapport verzonden naar ${accountantSettings.email}!`);
    } catch (error) {
      toast.error("Fout bij verzenden: " + (error.response?.data?.detail || error.message));
    } finally {
      setAccountantLoading(false);
    }
  };

  // Fetch available export periods
  const fetchExportPeriods = async () => {
    try {
      const response = await axios.get(`${API}/exports/available-periods`);
      setAvailablePeriods(response.data);
      if (response.data.months.length > 0) {
        setExportMonth(response.data.months[0].value);
      }
    } catch (error) {
      console.error("Failed to fetch export periods");
    }
  };

  // Handle export download
  const handleExport = async () => {
    setExportLoading(true);
    try {
      let url = `${API}/exports/bookings?`;
      
      if (exportType === "month" && exportMonth) {
        const [year, month] = exportMonth.split("-");
        url += `export_type=month&month=${month}&year=${year}`;
      } else if (exportType === "year" && exportYear) {
        url += `export_type=year&year=${exportYear}`;
      } else if (exportType === "custom" && exportStartDate && exportEndDate) {
        url += `export_type=custom&start_date=${exportStartDate}&end_date=${exportEndDate}`;
      }
      
      // Trigger download
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `FlevotaxiRunner_Export_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Export downloaded successfully!");
      setShowExportModal(false);
    } catch (error) {
      toast.error("Failed to generate export");
    } finally {
      setExportLoading(false);
    }
  };

  // Check push notification support on mount
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setPushSupported(supported);
    
    // Check if already subscribed
    if (supported) {
      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager.getSubscription().then(subscription => {
          setPushEnabled(!!subscription);
        });
      });
      
      // Register service worker
      navigator.serviceWorker.register('/sw.js').catch(err => {});
    }
  }, []);

  // Enable push notifications
  const enablePushNotifications = async () => {
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Please allow notifications to receive booking alerts');
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key
      const { data } = await axios.get(`${API}/vapid-public-key`);
      
      // Convert base64 to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey)
      });

      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });

      setPushEnabled(true);
      toast.success('Push notifications enabled! You will receive alerts for new bookings even when your screen is locked.');
      
      // Test notification
      setTimeout(async () => {
        await axios.post(`${API}/push/test`);
      }, 1000);
      
    } catch (error) {
      console.error('Push notification error:', error);
      toast.error('Failed to enable push notifications');
    }
  };

  // Disable push notifications
  const disablePushNotifications = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        const subscriptionJson = subscription.toJSON();
        await axios.delete(`${API}/push/unsubscribe`, {
          data: { endpoint: subscriptionJson.endpoint, keys: subscriptionJson.keys }
        });
        await subscription.unsubscribe();
      }
      
      setPushEnabled(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error disabling push:', error);
    }
  };

  useEffect(() => {
    if (isLoggedIn && audioEnabled && bookings.filter(b => b.status === 'pending').length > lastBookingCount) {
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
      audio.play().catch(console.error);
    }
    setLastBookingCount(bookings.filter(b => b.status === 'pending').length);
  }, [bookings, isLoggedIn, audioEnabled, lastBookingCount]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/driver/login`, { 
        pin,
        totp_code: totpCode || null
      });
      
      // Check if 2FA code is required (2FA is set up)
      if (response.data.requires_2fa && response.data.has_2fa) {
        setRequires2FA(true);
        setNeeds2FASetup(false);
        setPinType(response.data.pin_type);
        setPinValidated(true);
        toast.info("Voer uw 6-cijferige 2FA code in van Microsoft Authenticator");
        setLoading(false);
        return;
      }
      
      // Check if 2FA setup is needed (2FA is NOT set up)
      if (response.data.needs_setup && !response.data.has_2fa) {
        setPinType(response.data.pin_type);
        setPinValidated(true);
        setNeeds2FASetup(true);
        setRequires2FA(false);
        toast.info("2FA is nog niet ingesteld. Stel dit nu in voor extra beveiliging.");
        setLoading(false);
        return;
      }
      
      // Login successful (2FA was verified)
      if (response.data.driver) {
        setDriver(response.data.driver);
        setIsLoggedIn(true);
        setRequires2FA(false);
        setNeeds2FASetup(false);
        setPinValidated(false);
        setTotpCode("");
        fetchBookings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ongeldige PIN of 2FA code");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      // Use pinType that was validated during login attempt, or fall back to driver's pin_type
      const typeToSetup = pinType || driver?.pin_type || 'driver';
      const response = await axios.post(`${API}/driver/setup-2fa?pin_type=${typeToSetup}`);
      setQrCode2FA(response.data.qr_code);
      setSecret2FA(response.data.secret);
      setShow2FASetupModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kon 2FA niet instellen");
    }
  };

  const handleChangePin = async () => {
    if (newPin !== confirmNewPin) {
      toast.error("New PINs do not match");
      return;
    }
    if (newPin.length !== 4 || !/^\d+$/.test(newPin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    try {
      await axios.post(`${API}/driver/change-pin`, {
        current_pin: currentPin,
        new_pin: newPin,
        totp_code: changePinTotp
      });
      toast.success("PIN changed successfully!");
      setShowChangePinModal(false);
      setCurrentPin("");
      setNewPin("");
      setConfirmNewPin("");
      setChangePinTotp("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change PIN");
    }
  };

  const handleRequestPinReset = async (type) => {
    try {
      await axios.post(`${API}/driver/request-pin-reset`, { pin_type: type });
      toast.success("PIN reset email sent! Check your inbox.");
      setShowResetPinModal(false);
    } catch (error) {
      toast.error("Failed to send reset email");
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data.bookings);
    } catch (error) {
      console.error("Failed to fetch bookings");
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      const interval = setInterval(fetchBookings, 5000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  const handleAccept = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/accept`);
      toast.success("Booking accepted! Customer notified.");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to accept booking");
    }
  };

  const handleCancel = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/cancel-by-driver`);
      toast.success("Booking cancelled. Customer notified about refund.");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const handleStart = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/start`);
      toast.success("Trip started!");
      fetchBookings();
      
      if (navigator.geolocation) {
        navigator.geolocation.watchPosition(
          async (position) => {
            await axios.post(`${API}/driver/location`, {
              booking_id: bookingId,
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          console.error,
          { enableHighAccuracy: true }
        );
      }
    } catch (error) {
      toast.error("Failed to start trip");
    }
  };

  const handleComplete = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/complete`);
      toast.success("Trip completed!");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to complete booking");
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#FFD60A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Car className="w-8 h-8 text-[#FFD60A]" />
            </div>
            <CardTitle className="text-white">{t("driverLogin")}</CardTitle>
            <CardDescription>{t("enterPin")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-zinc-400">{t("pinCode")}</Label>
                <Input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter 4-digit PIN"
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white text-center text-2xl tracking-widest"
                  maxLength={4}
                  data-testid="driver-pin-input"
                  disabled={pinValidated}
                />
              </div>
              
              {/* Show 2FA code field ONLY if 2FA is already set up and required */}
              {requires2FA && (
                <div>
                  <Label className="text-zinc-400">2FA Code (Microsoft Authenticator)</Label>
                  <Input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="Voer 6-cijferige code in"
                    className="bg-zinc-950/50 border-zinc-800 h-12 text-white text-center text-2xl tracking-widest"
                    maxLength={6}
                    data-testid="driver-2fa-input"
                  />
                </div>
              )}
              
              {/* Login button - disabled when 2FA setup is needed */}
              {!needs2FASetup && (
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full"
                  data-testid="driver-login-btn"
                >
                  {loading ? "..." : t("login")}
                </Button>
              )}
              
              {/* Show 2FA Setup button when PIN is valid but 2FA is NOT set up */}
              {needs2FASetup && (
                <Button
                  type="button"
                  onClick={handleSetup2FA}
                  className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full"
                  data-testid="setup-2fa-btn"
                >
                  🔐 2FA Instellen (Microsoft Authenticator)
                </Button>
              )}
              
              {/* Back button when in validation mode */}
              {pinValidated && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPinValidated(false);
                    setRequires2FA(false);
                    setNeeds2FASetup(false);
                    setTotpCode("");
                    setPin("");
                  }}
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                >
                  ← Terug
                </Button>
              )}
              
              {!pinValidated && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowResetPinModal(true)}
                    className="w-full text-zinc-400 hover:text-white"
                  >
                    🔐 Forgot PIN? Reset here
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.location.href = '/home'}
                    className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                    data-testid="back-to-home-btn"
                  >
                    ← Terug naar Homepage
                  </Button>
                </>
              )}
            </form>
          </CardContent>
        </Card>
        
        {/* 2FA Setup Modal with QR Code */}
        {show2FASetupModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
              <CardHeader className="text-center">
                <CardTitle className="text-white">📱 2FA Instellen</CardTitle>
                <CardDescription>Scan de QR code met Microsoft Authenticator</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {qrCode2FA && (
                  <div className="flex justify-center">
                    <img 
                      src={qrCode2FA} 
                      alt="2FA QR Code" 
                      className="w-48 h-48 rounded-lg bg-white p-2"
                      data-testid="2fa-qr-code"
                    />
                  </div>
                )}
                
                <div className="text-center">
                  <p className="text-zinc-400 text-sm mb-2">Of voer deze code handmatig in:</p>
                  <p className="text-white font-mono text-sm break-all bg-zinc-900 p-3 rounded-lg">{secret2FA}</p>
                </div>
                
                <div className="bg-zinc-900/50 p-4 rounded-lg">
                  <p className="text-zinc-300 text-sm">
                    <strong>Stappen:</strong><br/>
                    1. Open Microsoft Authenticator<br/>
                    2. Tik op &quot;+&quot; → &quot;Ander account&quot;<br/>
                    3. Scan de QR code<br/>
                    4. Klik hieronder op &quot;Klaar&quot;
                  </p>
                </div>
                
                <Button
                  onClick={() => {
                    setShow2FASetupModal(false);
                    setQrCode2FA("");
                    setSecret2FA("");
                    // After setup, go back to login to verify with the new 2FA
                    setPinValidated(false);
                    setNeeds2FASetup(false);
                    setRequires2FA(false);
                    setPin("");
                    toast.success("2FA ingesteld! Log nu opnieuw in met uw PIN en 2FA code.");
                  }}
                  className="w-full bg-[#22C55E] text-white font-bold hover:bg-[#16A34A] rounded-full"
                  data-testid="2fa-setup-done-btn"
                >
                  ✅ Klaar - Ik heb de code gescand
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Reset PIN Modal */}
        {showResetPinModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white">🔐 Reset PIN</CardTitle>
                <CardDescription>Select which PIN you need to reset</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={() => handleRequestPinReset("driver")}
                  className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308]"
                >
                  Reset Driver Dashboard PIN
                </Button>
                <Button
                  onClick={() => handleRequestPinReset("master")}
                  variant="outline"
                  className="w-full border-zinc-700 text-white hover:bg-zinc-800"
                >
                  Reset Master PIN
                </Button>
                <Button
                  onClick={() => setShowResetPinModal(false)}
                  variant="ghost"
                  className="w-full text-zinc-400"
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  // Create test booking
  const handleTestMe = async () => {
    try {
      await axios.post(`${API}/bookings/test-run`);
      toast.success("Test booking created! Check your WhatsApp and notifications.");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to create test booking");
    }
  };

  // Dismiss test booking
  const handleDismissTest = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}/dismiss-test`);
      toast.success("Test booking dismissed!");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to dismiss test booking");
    }
  };

  // Separate test bookings from normal bookings for display
  const testBookings = bookings.filter(b => b.is_test);
  const normalBookings = bookings.filter(b => !b.is_test);
  
  // For tab filtering, only use normal bookings
  const filteredBookings = normalBookings.filter(b => {
    if (activeTab === "all") return true;
    return b.status === activeTab;
  });

  return (
    <div className="min-h-screen bg-[#09090B]">
      <nav className="bg-[#18181B] border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Car className="w-8 h-8 text-[#FFD60A]" />
            <span className="text-xl font-bold text-white">{t("driverDashboard")}</span>
            {/* Change PIN Button - Upper Left */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowChangePinModal(true)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white hidden md:flex"
            >
              🔑 Change PIN
            </Button>
            {/* Setup 2FA Button - Upper Left */}
            <Button
              size="sm"
              variant="outline"
              onClick={handleSetup2FA}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white hidden md:flex"
            >
              🔐 Setup 2FA
            </Button>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-end">
            {/* Test Me Button - Always visible */}
            <Button
              size="sm"
              onClick={handleTestMe}
              className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308]"
              data-testid="test-me-btn"
            >
              🧪 Test Me
            </Button>
            {/* Export Button - Always visible */}
            <Button
              size="sm"
              onClick={() => { setShowExportModal(true); fetchExportPeriods(); }}
              className="bg-[#3B82F6] text-white font-bold hover:bg-[#2563EB]"
            >
              📊 Export Bookings
            </Button>
            {/* Push toggle button */}
            {pushSupported && (
              <Button
                variant="outline"
                size="sm"
                onClick={pushEnabled ? disablePushNotifications : enablePushNotifications}
                className={`${pushEnabled ? 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/10' : 'border-red-500 text-red-500'}`}
                data-testid="toggle-push-btn"
              >
                {pushEnabled ? <Bell className="w-4 h-4 mr-1" /> : <BellOff className="w-4 h-4 mr-1" />}
                <span className="hidden sm:inline">{pushEnabled ? 'Push ON' : 'Push OFF'}</span>
              </Button>
            )}
            {/* Sound toggle button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAudioEnabled(!audioEnabled)}
              className={`${audioEnabled ? 'border-[#FFD60A] text-[#FFD60A]' : 'border-zinc-700 text-zinc-400'}`}
              data-testid="toggle-sound-btn"
            >
              <Volume2 className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">{audioEnabled ? 'Geluid Uit' : 'Geluid Uit'}</span>
            </Button>
            {/* FlevotaxiRunner Driver Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10">
                  FlevotaxiRunner Driver
                  <MoreVertical className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700 text-white w-56">
                <DropdownMenuLabel className="text-zinc-400">
                  FlevotaxiRunner Driver Menu
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-700" />
                
                {/* Settings options */}
                <DropdownMenuItem 
                  onClick={() => setShowChangePinModal(true)}
                  className="cursor-pointer hover:bg-zinc-800"
                >
                  <Key className="w-4 h-4 mr-2" />
                  PIN Wijzigen
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSetup2FA}
                  className="cursor-pointer hover:bg-zinc-800"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  2FA Instellen
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    toast.info(
                      <div className="text-sm">
                        <p className="font-bold mb-2">📲 Installeer als App:</p>
                        <p className="mb-1"><strong>iPhone:</strong> Tik op Deel → Zet op beginscherm</p>
                        <p><strong>Android:</strong> Tik op ⋮ → Toevoegen aan startscherm</p>
                      </div>,
                      { duration: 8000 }
                    );
                  }}
                  className="cursor-pointer hover:bg-zinc-800"
                >
                  <Smartphone className="w-4 h-4 mr-2 text-[#22C55E]" />
                  Installeer als App
                </DropdownMenuItem>
                
                {/* Accountant option - only for master */}
                {driver?.is_master && (
                  <>
                    <DropdownMenuSeparator className="bg-zinc-700" />
                    <DropdownMenuItem 
                      onClick={() => { fetchAccountantSettings(); setShowAccountantModal(true); }}
                      className="cursor-pointer hover:bg-zinc-800"
                    >
                      <FileText className="w-4 h-4 mr-2 text-[#3B82F6]" />
                      Accountant Instellingen
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleSendAccountantReport}
                      disabled={accountantLoading}
                      className="cursor-pointer hover:bg-zinc-800"
                    >
                      <Mail className="w-4 h-4 mr-2 text-[#22C55E]" />
                      {accountantLoading ? 'Verzenden...' : 'Rapport naar Accountant'}
                    </DropdownMenuItem>
                  </>
                )}
                
                <DropdownMenuSeparator className="bg-zinc-700" />
                
                {/* Logout */}
                <DropdownMenuItem 
                  onClick={() => {
                    setIsLoggedIn(false);
                    setDriver(null);
                    setPin("");
                    setTotpCode("");
                    toast.success("Uitgelogd");
                  }}
                  className="cursor-pointer hover:bg-red-500/20 text-red-400"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Uitloggen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#18181B] border-zinc-800 w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#3B82F6]" />
                Export Completed Bookings
              </CardTitle>
              <CardDescription>Generate Excel report with booking details, revenue breakdown, and tax calculations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Export Type Tabs */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setExportType("month")}
                  className={exportType === "month" ? "bg-[#3B82F6] text-white" : "bg-zinc-800 text-zinc-400"}
                >
                  📅 Month
                </Button>
                <Button
                  size="sm"
                  onClick={() => setExportType("year")}
                  className={exportType === "year" ? "bg-[#3B82F6] text-white" : "bg-zinc-800 text-zinc-400"}
                >
                  📆 Year
                </Button>
                <Button
                  size="sm"
                  onClick={() => setExportType("custom")}
                  className={exportType === "custom" ? "bg-[#3B82F6] text-white" : "bg-zinc-800 text-zinc-400"}
                >
                  🎯 Custom Range
                </Button>
              </div>

              {/* Month Selection */}
              {exportType === "month" && (
                <div>
                  <Label className="text-zinc-400">Select Month</Label>
                  <select
                    value={exportMonth}
                    onChange={(e) => setExportMonth(e.target.value)}
                    className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md p-2 text-white"
                  >
                    {availablePeriods.months.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Year Selection */}
              {exportType === "year" && (
                <div>
                  <Label className="text-zinc-400">Select Year</Label>
                  <select
                    value={exportYear}
                    onChange={(e) => setExportYear(parseInt(e.target.value))}
                    className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md p-2 text-white"
                  >
                    {availablePeriods.years.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Custom Date Range */}
              {exportType === "custom" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Start Date</Label>
                    <Input
                      type="date"
                      value={exportStartDate}
                      onChange={(e) => setExportStartDate(e.target.value)}
                      className="bg-zinc-950/50 border-zinc-800 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">End Date</Label>
                    <Input
                      type="date"
                      value={exportEndDate}
                      onChange={(e) => setExportEndDate(e.target.value)}
                      className="bg-zinc-950/50 border-zinc-800 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Export Info */}
              <div className="bg-zinc-900 p-3 rounded-lg">
                <p className="text-sm text-zinc-400">The export includes:</p>
                <ul className="text-xs text-zinc-500 mt-1 space-y-1">
                  <li>• All booking details (customer, pickup, destination)</li>
                  <li>• Distance and duration per ride</li>
                  <li>• Payment type and status</li>
                  <li>• <span className="text-green-400">Gross amount, Tax (9%), Net amount</span></li>
                  <li>• Summary with totals for tax authorities</li>
                </ul>
              </div>

              {/* Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={handleExport}
                  disabled={exportLoading}
                  className="flex-1 bg-[#3B82F6] text-white font-bold hover:bg-[#2563EB]"
                >
                  {exportLoading ? "Generating..." : "📥 Download Excel"}
                </Button>
                <Button
                  onClick={() => setShowExportModal(false)}
                  variant="outline"
                  className="flex-1 border-zinc-700 text-white"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Accountant Settings Modal */}
      {showAccountantModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#18181B] border-zinc-800 w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#3B82F6]" />
                Accountant Instellingen
              </CardTitle>
              <CardDescription>Configureer accountant gegevens voor maandelijkse rapporten</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Naam *</Label>
                  <Input
                    type="text"
                    value={accountantSettings.name}
                    onChange={(e) => setAccountantSettings({...accountantSettings, name: e.target.value})}
                    placeholder="Jan de Boer"
                    className="bg-zinc-950/50 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Email *</Label>
                  <Input
                    type="email"
                    value={accountantSettings.email}
                    onChange={(e) => setAccountantSettings({...accountantSettings, email: e.target.value})}
                    placeholder="accountant@kantoor.nl"
                    className="bg-zinc-950/50 border-zinc-800 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Bedrijfsnaam</Label>
                  <Input
                    type="text"
                    value={accountantSettings.company_name}
                    onChange={(e) => setAccountantSettings({...accountantSettings, company_name: e.target.value})}
                    placeholder="Boekhouding B.V."
                    className="bg-zinc-950/50 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Telefoon</Label>
                  <Input
                    type="tel"
                    value={accountantSettings.phone}
                    onChange={(e) => setAccountantSettings({...accountantSettings, phone: e.target.value})}
                    placeholder="06-12345678"
                    className="bg-zinc-950/50 border-zinc-800 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Notities</Label>
                <textarea
                  value={accountantSettings.notes}
                  onChange={(e) => setAccountantSettings({...accountantSettings, notes: e.target.value})}
                  placeholder="Extra notities..."
                  className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-md p-2 h-20"
                />
              </div>
              
              <div className="bg-zinc-900 p-3 rounded-lg">
                <p className="text-sm text-zinc-400">📧 Maandelijks rapport bevat:</p>
                <ul className="text-xs text-zinc-500 mt-1 space-y-1">
                  <li>• Overzicht alle voltooide ritten</li>
                  <li>• Totale omzet incl. en excl. BTW</li>
                  <li>• BTW afdracht berekening (9%)</li>
                  <li>• Samenvatting voor belastingaangifte</li>
                </ul>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSaveAccountant}
                  disabled={accountantLoading}
                  className="flex-1 bg-[#3B82F6] text-white font-bold hover:bg-[#2563EB]"
                >
                  {accountantLoading ? "Opslaan..." : "💾 Opslaan"}
                </Button>
                <Button
                  onClick={() => setShowAccountantModal(false)}
                  variant="outline"
                  className="flex-1 border-zinc-700 text-white"
                >
                  Annuleren
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Change PIN Modal */}
      {showChangePinModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white">🔑 Change Driver PIN</CardTitle>
              <CardDescription>Enter your current PIN and choose a new 4-digit PIN</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-400">Current PIN</Label>
                <Input
                  type="password"
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value)}
                  placeholder="Enter current PIN"
                  className="bg-zinc-950/50 border-zinc-800 text-white"
                  maxLength={4}
                />
              </div>
              <div>
                <Label className="text-zinc-400">New PIN</Label>
                <Input
                  type="password"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="Enter new 4-digit PIN"
                  className="bg-zinc-950/50 border-zinc-800 text-white"
                  maxLength={4}
                />
              </div>
              <div>
                <Label className="text-zinc-400">Confirm New PIN</Label>
                <Input
                  type="password"
                  value={confirmNewPin}
                  onChange={(e) => setConfirmNewPin(e.target.value)}
                  placeholder="Confirm new PIN"
                  className="bg-zinc-950/50 border-zinc-800 text-white"
                  maxLength={4}
                />
              </div>
              <div>
                <Label className="text-zinc-400">2FA Code (from Microsoft Authenticator)</Label>
                <Input
                  type="text"
                  value={changePinTotp}
                  onChange={(e) => setChangePinTotp(e.target.value)}
                  placeholder="Enter 6-digit 2FA code"
                  className="bg-zinc-950/50 border-zinc-800 text-white"
                  maxLength={6}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleChangePin}
                  className="flex-1 bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308]"
                >
                  Change PIN
                </Button>
                <Button
                  onClick={() => {
                    setShowChangePinModal(false);
                    setCurrentPin("");
                    setNewPin("");
                    setConfirmNewPin("");
                    setChangePinTotp("");
                  }}
                  variant="outline"
                  className="flex-1 border-zinc-700 text-white"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 2FA Setup Modal */}
      {show2FASetupModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white">📱 Setup 2FA</CardTitle>
              <CardDescription>Scan the QR code with Microsoft Authenticator</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {qrCode2FA && (
                <div className="flex justify-center">
                  <img src={qrCode2FA} alt="2FA QR Code" className="w-48 h-48" />
                </div>
              )}
              <div className="bg-zinc-900 p-3 rounded-lg">
                <p className="text-xs text-zinc-400 mb-1">Manual entry code:</p>
                <p className="text-white font-mono text-sm break-all">{secret2FA}</p>
              </div>
              <p className="text-sm text-zinc-400">
                1. Open Microsoft Authenticator<br />
                2. Tap + to add account<br />
                3. Choose &quot;Other account&quot;<br />
                4. Scan this QR code
              </p>
              <Button
                onClick={() => {
                  setShow2FASetupModal(false);
                  setQrCode2FA("");
                  setSecret2FA("");
                  toast.success("2FA setup complete! You'll be asked for a code once daily.");
                }}
                className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308]"
              >
                Done
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Push Notification Banner */}
        {!pushEnabled && pushSupported && (
          <Card className="bg-gradient-to-r from-[#FFD60A]/20 to-[#FFD60A]/5 border-[#FFD60A]/30 mb-6">
            <CardContent className="p-4 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#FFD60A]/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🔔</span>
                </div>
                <div>
                  <p className="text-white font-semibold">Enable Push Notifications</p>
                  <p className="text-zinc-400 text-sm">Get alerts for new bookings even when your screen is locked!</p>
                </div>
              </div>
              <Button
                onClick={enablePushNotifications}
                className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308]"
                data-testid="enable-push-btn"
              >
                Enable Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: t("pending"), value: normalBookings.filter(b => b.status === 'pending').length, color: "warning" },
            { label: t("accepted"), value: normalBookings.filter(b => b.status === 'accepted').length, color: "info" },
            { label: t("inProgress"), value: normalBookings.filter(b => b.status === 'in_progress').length, color: "primary" },
            { label: t("completed"), value: normalBookings.filter(b => b.status === 'completed').length, color: "success" },
          ].map((stat, i) => (
            <Card key={i} className="bg-[#18181B] border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-zinc-500 text-sm">{stat.label}</p>
                <p className="text-3xl font-black text-white">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bookings Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#18181B] border border-zinc-800 mb-6">
            <TabsTrigger value="pending" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black">
              {t("pending")} ({normalBookings.filter(b => b.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger value="accepted" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black">
              {t("accepted")}
            </TabsTrigger>
            <TabsTrigger value="in_progress" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black">
              {t("inProgress")}
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black">
              {t("completed")}
            </TabsTrigger>
            <TabsTrigger value="all" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black">
              {t("all")}
            </TabsTrigger>
          </TabsList>

          <div className="space-y-4">
            {/* Test Bookings - shown at top with special styling */}
            {testBookings.map((booking) => (
              <Card 
                key={booking.id} 
                className="bg-[#FFD60A]/10 border-[#FFD60A]/40 border-2" 
                data-testid={`test-booking-${booking.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge className="bg-[#FFD60A]/20 text-[#FFD60A] border-[#FFD60A]/50">
                          🧪 TEST BOOKING
                        </Badge>
                        <span className="text-zinc-400 font-mono text-sm">{booking.date} • {booking.time}</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-[#FFD60A] mt-0.5" />
                          <div>
                            <p className="text-zinc-400 text-sm">{t("from")}</p>
                            <p className="text-white">{booking.pickup_address}, {booking.pickup_city}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Navigation className="w-5 h-5 text-[#22C55E] mt-0.5" />
                          <div>
                            <p className="text-zinc-400 text-sm">{t("to")}</p>
                            <p className="text-white">{booking.destination_address}, {booking.destination_city}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-zinc-300">
                          <Users className="w-4 h-4 inline mr-1" />
                          {booking.passenger_count} {t("passengers").toLowerCase()}
                        </span>
                        <span className="text-zinc-300">
                          <Phone className="w-4 h-4 inline mr-1" />
                          <span className="text-[#FFD60A]">{booking.phone_number}</span>
                        </span>
                        <span className="text-[#FFD60A] font-bold">€{booking.price}</span>
                        <span className="text-zinc-400 italic">👤 {booking.customer_name}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => handleDismissTest(booking.id)}
                        className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] whitespace-nowrap"
                        data-testid={`dismiss-test-btn-${booking.id}`}
                      >
                        Die Ekkoh Test is al klaar met JE
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Normal Bookings */}
            {filteredBookings.length === 0 && testBookings.length === 0 ? (
              <Card className="bg-[#18181B] border-zinc-800">
                <CardContent className="p-8 text-center">
                  <Car className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">{t("noBookings")}</p>
                </CardContent>
              </Card>
            ) : (
              filteredBookings.map((booking) => (
                <Card key={booking.id} className="bg-[#18181B] border-zinc-800 hover:border-[#FFD60A]/30 transition-colors" data-testid={`booking-card-${booking.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className={`status-${booking.status}`}>
                            {booking.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                          <span className="text-zinc-500 font-mono text-sm">{booking.date} • {booking.time}</span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-5 h-5 text-[#FFD60A] mt-0.5" />
                            <div>
                              <p className="text-zinc-500 text-sm">{t("from")}</p>
                              <p className="text-white">{booking.pickup_address}, {booking.pickup_city}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Navigation className="w-5 h-5 text-[#22C55E] mt-0.5" />
                            <div>
                              <p className="text-zinc-500 text-sm">{t("to")}</p>
                              <p className="text-white">{booking.destination_address}, {booking.destination_city}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="text-zinc-400">
                            <Users className="w-4 h-4 inline mr-1" />
                            {booking.passenger_count} {t("passengers").toLowerCase()}
                          </span>
                          <span className="text-zinc-400">
                            <Phone className="w-4 h-4 inline mr-1" />
                            <a href={`tel:${booking.phone_number}`} className="text-[#FFD60A] hover:underline">
                              {booking.phone_number}
                            </a>
                          </span>
                          <span className="text-[#FFD60A] font-bold">€{booking.price}</span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {booking.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleAccept(booking.id)}
                              className="bg-[#22C55E] text-white font-bold hover:bg-[#16A34A] min-w-[140px]"
                              data-testid={`accept-btn-${booking.id}`}
                            >
                              {t("accept")}
                            </Button>
                            <Button
                              onClick={() => handleCancel(booking.id)}
                              className="bg-[#EF4444] text-white font-bold hover:bg-[#DC2626] min-w-[140px]"
                              data-testid={`cancel-btn-${booking.id}`}
                            >
                              {t("cancel")}
                            </Button>
                          </>
                        )}
                        {booking.status === 'accepted' && (
                          <Button
                            onClick={() => handleStart(booking.id)}
                            className="bg-[#3B82F6] text-white font-bold hover:bg-[#2563EB] min-w-[140px]"
                            data-testid={`start-btn-${booking.id}`}
                          >
                            {t("startTrip")}
                          </Button>
                        )}
                        {booking.status === 'in_progress' && (
                          <Button
                            onClick={() => handleComplete(booking.id)}
                            className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] min-w-[140px]"
                            data-testid={`complete-btn-${booking.id}`}
                          >
                            {t("complete")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </Tabs>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
};

// ============ ADMIN PANEL ============
const AdminPanel = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.require_2fa_setup) {
        const setupResponse = await axios.post(`${API}/admin/setup-2fa`, { username, password });
        setQrCode(setupResponse.data.qr_code);
        setStep("setup2fa");
      } else if (response.data.require_2fa) {
        setStep("verify2fa");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify-2fa`, { username, totp_code: totpCode });
      toast.success("2FA verified!");
      setStep("dashboard");
      fetchData();
    } catch (error) {
      toast.error("Invalid 2FA code");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/stats`)
      ]);
      setBookings(bookingsRes.data.bookings);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch data");
    }
  };

  if (step === "login") {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-white">{t("adminLogin")}</CardTitle>
            <CardDescription>{t("enterCredentials")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-zinc-400">{t("username")}</Label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">{t("password")}</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full"
                data-testid="admin-login-btn"
              >
                {loading ? "..." : t("login")}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  if (step === "setup2fa") {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-white">{t("setup2FA")}</CardTitle>
            <CardDescription>{t("scan2FA")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {qrCode && (
              <img src={qrCode} alt="2FA QR Code" className="mx-auto rounded-xl" />
            )}
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <Label className="text-zinc-400">{t("enter6Digit")}</Label>
                <Input
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white text-center text-2xl tracking-widest"
                  maxLength={6}
                  data-testid="admin-totp-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full"
                data-testid="admin-verify-btn"
              >
                {loading ? t("verifying") : t("verifyLogin")}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  if (step === "verify2fa") {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-white">{t("enter2FACode")}</CardTitle>
            <CardDescription>{t("open2FAApp")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <Label className="text-zinc-400">{t("enter6Digit")}</Label>
                <Input
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  className="bg-zinc-950/50 border-zinc-800 h-12 text-white text-center text-2xl tracking-widest"
                  maxLength={6}
                  data-testid="admin-totp-verify-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full"
              >
                {loading ? t("verifying") : t("verify")}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#09090B]">
      <nav className="bg-[#18181B] border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Car className="w-8 h-8 text-[#FFD60A]" />
            <span className="text-xl font-bold text-white">{t("adminPanel")}</span>
          </div>
          <Badge className="bg-[#FFD60A]/10 text-[#FFD60A] border-[#FFD60A]/30">
            {username}
          </Badge>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-[#18181B] border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-zinc-500 text-sm">{t("total")}</p>
                <p className="text-3xl font-black text-white">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#18181B] border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-zinc-500 text-sm">{t("pending")}</p>
                <p className="text-3xl font-black text-[#F59E0B]">{stats.pending}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#18181B] border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-zinc-500 text-sm">{t("accepted")}</p>
                <p className="text-3xl font-black text-[#3B82F6]">{stats.accepted}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#18181B] border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-zinc-500 text-sm">{t("inProgress")}</p>
                <p className="text-3xl font-black text-[#8B5CF6]">{stats.in_progress}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#18181B] border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-zinc-500 text-sm">{t("completed")}</p>
                <p className="text-3xl font-black text-[#22C55E]">{stats.completed}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="bg-[#18181B] border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">{t("allBookings")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left text-zinc-500 font-medium py-3 px-4">{t("date")}</th>
                    <th className="text-left text-zinc-500 font-medium py-3 px-4">{t("customer")}</th>
                    <th className="text-left text-zinc-500 font-medium py-3 px-4">{t("route")}</th>
                    <th className="text-left text-zinc-500 font-medium py-3 px-4">{t("status")}</th>
                    <th className="text-left text-zinc-500 font-medium py-3 px-4">{t("price")}</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-zinc-800/50 hover:bg-zinc-900/50">
                      <td className="py-3 px-4 text-white">{booking.date} {booking.time}</td>
                      <td className="py-3 px-4">
                        <p className="text-white">{booking.customer_name}</p>
                        <p className="text-zinc-500 text-sm">{booking.phone_number}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-white">{booking.pickup_city} → {booking.destination_city}</p>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={`status-${booking.status}`}>
                          {booking.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-[#FFD60A] font-bold">€{booking.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
};

// ============ RESET PIN PAGE ============
const ResetPinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token");
  const pinType = searchParams.get("type");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPin !== confirmPin) {
      toast.error("PINs do not match");
      return;
    }
    if (newPin.length !== 4 || !/^\d+$/.test(newPin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/driver/confirm-pin-reset`, {
        token,
        new_pin: newPin
      });
      setSuccess(true);
      toast.success("PIN changed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset PIN");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#22C55E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#22C55E]" />
            </div>
            <CardTitle className="text-white">PIN Reset Successful!</CardTitle>
            <CardDescription>
              Your {pinType === "master" ? "Master PIN" : "Driver Dashboard PIN"} has been changed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/driver")}
              className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full"
            >
              Go to Driver Dashboard
            </Button>
          </CardContent>
        </Card>
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
      <Card className="bg-[#18181B] border-zinc-800 w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-white">🔐 Reset {pinType === "master" ? "Master PIN" : "Driver Dashboard PIN"}</CardTitle>
          <CardDescription>Enter your new 4-digit PIN</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-400">New PIN</Label>
              <Input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                placeholder="Enter new 4-digit PIN"
                className="bg-zinc-950/50 border-zinc-800 h-12 text-white text-center text-2xl tracking-widest"
                maxLength={4}
              />
            </div>
            <div>
              <Label className="text-zinc-400">Confirm New PIN</Label>
              <Input
                type="password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value)}
                placeholder="Confirm new PIN"
                className="bg-zinc-950/50 border-zinc-800 h-12 text-white text-center text-2xl tracking-widest"
                maxLength={4}
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] rounded-full"
            >
              {loading ? "Resetting..." : "Reset PIN"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Toaster richColors position="top-right" />
    </div>
  );
};

// ============ FLOATING LANGUAGE BUTTON ============
const FloatingLanguageButton = () => {
  const { t, lang, setLang, availableLanguages } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  // Use availableLanguages from context (imported from locales)
  const languages = availableLanguages;

  const currentLang = languages.find(l => l.code === lang) || languages[0];
  
  // Don't show on driver/admin pages
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (currentPath.startsWith('/driver') || currentPath.startsWith('/admin') || currentPath.startsWith('/reset-pin')) {
    return null;
  }
  
  // Don't show if no language selected yet
  if (!lang) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50">
      {isOpen && (
        <div className="absolute bottom-16 left-0 bg-[#18181B] border border-zinc-700 rounded-xl shadow-2xl overflow-hidden mb-2 min-w-[180px] animate-in slide-in-from-bottom-2 fade-in duration-200">
          <div className="p-2">
            <p className="text-xs text-zinc-500 px-3 py-1 uppercase tracking-wide">{t("changeLanguage")}</p>
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => {
                  setLang(language.code);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  lang === language.code 
                    ? "bg-[#FFD60A]/20 text-[#FFD60A]" 
                    : "hover:bg-zinc-800 text-white"
                }`}
              >
                <span className="text-xl">{language.flag}</span>
                <span className="text-sm font-medium">{language.name}</span>
                {lang === language.code && <Check className="w-4 h-4 ml-auto" />}
              </button>
            ))}
          </div>
        </div>
      )}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-[#18181B] border border-zinc-700 hover:border-[#FFD60A]/50 text-white px-4 py-3 rounded-full shadow-xl transition-all hover:scale-105 active:scale-95"
        data-testid="floating-language-btn"
      >
        <span className="text-xl">{currentLang.flag}</span>
        <Globe className="w-4 h-4 text-[#FFD60A]" />
      </button>
    </div>
  );
};

// ============ APP CONTENT WRAPPER ============
const AppContent = () => {
  const { lang } = useTranslation();
  const [showSplash, setShowSplash] = useState(true);
  const [splashComplete, setSplashComplete] = useState(false);
  const langSelected = !!lang;
  
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
    setSplashComplete(true);
  }, []);

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  // Only skip splash for specific routes (driver dashboard, admin, tracking, payment flows)
  const skipSplashRoutes = ['/payment', '/booking-success', '/track/', '/driver', '/admin', '/reset-pin'];
  const shouldSkipSplash = skipSplashRoutes.some(route => currentPath.startsWith(route));

  return (
    <>
      {/* Always show splash on main page visit - 7 seconds with logo animation */}
      {showSplash && !shouldSkipSplash && (
        <SplashScreen onComplete={handleSplashComplete} />
      )}
      {/* Only show content after splash completes OR on skip routes */}
      {(splashComplete || shouldSkipSplash) && (
        <>
          <FloatingLanguageButton />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<LandingPage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/book-route" element={<FixedRouteBookingPage />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/booking-success" element={<BookingSuccessPage />} />
            <Route path="/track/:bookingId" element={<TrackingPage />} />
            <Route path="/driver" element={<DriverDashboard />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/reset-pin" element={<ResetPinPage />} />
          </Routes>
        </>
      )}
    </>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;